import React from 'react';
import { Link } from 'react-router-dom';
import { UserPlusIcon, ArrowRightOnRectangleIcon } from '@heroicons/react/24/outline';

/**
 * Step 1: Account Setup Landing Page
 *
 * Simplified page that directs users to either:
 * - Create a new account (navigates to /register)
 * - Sign in with existing account (navigates to /login)
 *
 * Registration and login are handled by their respective dedicated pages.
 * After successful registration, users are redirected to Step 2.
 */
const Step1Page = () => {
  return (
    <div className="step-page min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header with back navigation */}
      <div className="bg-white dark:bg-gray-800 shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Link
                to="/cdi-trial"
                className="text-primary-600 hover:text-primary-700 flex items-center text-sm font-medium"
              >
                ← Back to CDI Trial
              </Link>
              <div className="h-4 w-px bg-gray-300 dark:bg-gray-600"></div>
              <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
                Step 1: Account Setup
              </h1>
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400">
              2 minutes
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">

          {/* Progress indicator */}
          <div className="mb-8">
            <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-400 mb-2">
              <span>Step 1 of 3</span>
              <span>33% Complete</span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
              <div className="bg-primary-600 h-2 rounded-full" style={{ width: '33%' }}></div>
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">

            {/* Left side - Account options */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-8">
              <h2 className="text-2xl font-bold mb-4 text-gray-900 dark:text-white">
                Get Started
              </h2>
              <p className="text-gray-600 dark:text-gray-400 mb-8">
                To begin the CDI Trial, you'll need an account. Choose an option below to continue.
              </p>

              {/* Create Account Button */}
              <div className="space-y-4">
                <Link
                  to="/register"
                  className="w-full flex items-center justify-center px-6 py-4 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-medium transition-colors shadow-sm"
                >
                  <UserPlusIcon className="h-5 w-5 mr-3" />
                  Create an Account
                </Link>

                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-300 dark:border-gray-600"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-4 bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400">
                      or
                    </span>
                  </div>
                </div>

                {/* Sign In Button */}
                <Link
                  to="/login"
                  className="w-full flex items-center justify-center px-6 py-4 border-2 border-primary-600 text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg font-medium transition-colors"
                >
                  <ArrowRightOnRectangleIcon className="h-5 w-5 mr-3" />
                  Sign In to Existing Account
                </Link>
              </div>

              <p className="mt-6 text-sm text-gray-500 dark:text-gray-400 text-center">
                After signing in or creating an account, you'll continue to Step 2.
              </p>
            </div>

            {/* Right side - Information */}
            <div className="space-y-6">
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
                <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
                  🔒 What We'll Cover in This Step
                </h3>
                <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                  <li>• Sign up with email or sign in to existing account</li>
                  <li>• Accept Terms and Privacy Policy</li>
                  <li>• Acknowledge: trial accepts de-identified data only (no PHI/PII)</li>
                  <li>• Enter your organization name</li>
                </ul>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
                <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
                  ✅ Security & Compliance
                </h3>
                <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                  <p>Your account will be created with:</p>
                  <ul className="space-y-1 ml-4">
                    <li>• HIPAA-compliant infrastructure</li>
                    <li>• End-to-end encryption</li>
                    <li>• Multi-factor authentication support</li>
                    <li>• SOC 2 certified environment</li>
                  </ul>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
                <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
                  ⚠️ Important Note
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  This trial environment is designed for <strong>de-identified data only</strong>.
                  Please ensure your data contains no PHI/PII before proceeding to the next steps.
                </p>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
                <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
                  📋 Next Steps Preview
                </h3>
                <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                  <p>After account setup, you'll:</p>
                  <ul className="space-y-1 ml-4">
                    <li>→ Choose your data source (sample or upload)</li>
                    <li>→ Prepare and upload your claims data</li>
                    <li>→ Configure processing parameters</li>
                    <li>→ Review comprehensive results</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Navigation buttons */}
          <div className="flex justify-between mt-8">
            <Link
              to="/cdi-trial"
              className="px-6 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              ← Back to Trial Overview
            </Link>
            <Link
              to="/cdi-trial/step-2"
              className="px-6 py-3 bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 rounded-lg cursor-not-allowed"
              onClick={(e) => e.preventDefault()}
              title="Complete account setup first"
            >
              Continue to Step 2 →
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Step1Page;
